import { resolveDisplayRightsRowAction } from '@/components/display-rights/display-rights-status';
import { useTranslation } from 'react-i18next';
import { useActiveContactAds } from '../../profile/use-active-contact-ads';
import { useContactAdDrilldown } from './use-contact-ad-drilldown';

/** The gesture titles, by the shared composition vocabulary. */
const TITLE_KEY = {
  propose: 'displayRights.composer.title',
  revise: 'displayRights.composer.titleRevise',
  renew: 'displayRights.composer.titleRenew',
} as const;

/**
 * The title the drawer's context bar shows while the composer is open.
 *
 * The composer used to carry its own header — a second title under
 * "Partnership Ads" and a second back arrow under the bar's own. One screen,
 * one title, one way back: the gesture replaces the section name in the bar,
 * and the bar's arrow does the unwinding it was already wired for.
 *
 * The gesture comes from the same resolver as the button the brand clicked, so
 * the title always names the move they asked for. Reads the ads cache the view
 * has already filled — `enabled` is the composer being open, so this fires no
 * request of its own and none at all on any other view.
 */
export function useContactAdComposeTitle(): string | null {
  const { t } = useTranslation();
  const { authorizationId, isComposing } = useContactAdDrilldown();
  const { ads } = useActiveContactAds(isComposing);

  if (!isComposing) {
    return null;
  }

  const ad = ads.find((candidate) => candidate.id === authorizationId);
  if (!ad) {
    // The list has not landed yet, or the id is stale: name the surface rather
    // than flash a wrong gesture.
    return t('displayRights.composer.title');
  }

  const action = resolveDisplayRightsRowAction(ad.displayRights, ad.authorizationStatus);

  return t(action === 'none' ? 'displayRights.composer.title' : TITLE_KEY[action]);
}
